import { useMemo, useState } from "react";
import { categories, webtoons } from "@/mocks/webtoons";

interface RecommendGridProps {
  query: string;
  onSearch: (q: string) => void;
}

export default function RecommendGrid({ query, onSearch }: RecommendGridProps) {
  const [active, setActive] = useState("全部");

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return webtoons.filter((w) => {
      const matchCat = active === "全部" || w.genre === active;
      const matchKw =
        !kw ||
        w.title.toLowerCase().includes(kw) ||
        w.tags.some((t) => t.toLowerCase().includes(kw)) ||
        w.genreLabel.toLowerCase().includes(kw);
      return matchCat && matchKw;
    });
  }, [active, query]);

  return (
    <section id="list" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="flex flex-col gap-2">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600">
            <i className="ri-grid-fill"></i>
            分类推荐
          </span>
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            按你的心情，选一部好韩漫
          </h2>
          <p className="text-sm text-foreground-600 md:text-base">
            结合分类筛选与关键词搜索，快速找到评分高、口碑好的那一部。
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === c
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-64">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-500"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="筛选当前分类"
              className="h-11 w-full rounded-full border border-background-300 bg-background-100 pl-10 pr-4 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-lg bg-background-100 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-400"></i>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-800">
              没找到匹配的韩漫
            </p>
            <p className="mt-1 text-sm text-foreground-600">
              换个关键词或分类试试，比如「治愈」「悬疑」「完结」。
            </p>
            <button
              type="button"
              onClick={() => {
                setActive("全部");
                onSearch("");
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((w) => (
              <article
                key={w.id}
                className="group flex flex-col overflow-hidden rounded-lg bg-background-100 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  <img
                    src={w.cover}
                    alt={`${w.title}韩漫推荐封面`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span
                    className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-medium text-background-50 ${
                      w.status === "完结"
                        ? "bg-accent-600"
                        : w.status === "连载"
                          ? "bg-primary-500"
                          : "bg-secondary-600"
                    }`}
                  >
                    {w.status}
                  </span>
                  <span className="absolute bottom-3 right-3 rounded-full bg-background-950/70 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
                    <i className="ri-star-fill mr-1 text-secondary-400"></i>
                    {w.score.toFixed(1)}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
                      {w.genreLabel}
                    </span>
                    <span className="text-xs text-foreground-500">{w.episode}话</span>
                  </div>
                  <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">
                    {w.title}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                    {w.summary}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {w.tags.map((t) => (
                      <span key={t} className="text-xs text-accent-700">
                        #{t}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}